import React, { useState, useEffect } from 'react';
import { Product, ProductGroup, ProductType, PocketAssignment, ColumnConfiguration, ColumnType } from '../types';
import Modal from './Modal';
import SingleProductSelectionInput from './SingleProductSelectionInput';
import PocketAssignmentInput from './PocketAssignmentInput';
import DashboardMetricsTab from './DashboardMetricsTab';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import { usePreferences } from '../contexts/PreferencesContext';

interface InputFieldProps {
  label: string;
  name: string;
  value: string | number; // Allow number for numeric inputs when using type="number"
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  required?: boolean;
  type?: string; // Add type prop
  step?: string; // Add step for number inputs
  min?: string; // Add min for number inputs
  max?: string; // Add max for number inputs
}

const InputField: React.FC<InputFieldProps> = ({ label, name, value, onChange, error, required, type = 'text', step, min, max }) => (
  <div className="mb-5">
    <label htmlFor={name} className="block text-sm font-semibold text-slate-700 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      step={step}
      min={min}
      max={max}
      className={`w-full px-4 py-2.5 border-2 rounded-lg shadow-sm transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${error ? 'border-red-300 bg-red-50' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
      aria-required={required}
      aria-describedby={error ? `${name}-error` : undefined}
    />
    {error && <p id={`${name}-error`} className="mt-1.5 text-xs text-red-600 font-medium">{error}</p>}
  </div>
);

interface GroupFormData {
  groupId: string;
  groupName: string;
  showOnDashboard: boolean;
  showOnReports: boolean;
  infeedDisplayName: string;
  infeedProductId: string; // Single product ID for infeed
  pocketAssignments: PocketAssignment[]; // Array of pocket assignments
  infeedBatchWeight: number | ''; // Batch weight in LBS
  showInfeedPph: boolean; // Whether to display infeed PPH
  infeedPphDisplayName: string; // Display name for infeed PPH
  showInfeedPpm: boolean; // Whether to display infeed PPM
  infeedPpmDisplayName: string; // Display name for infeed PPM
  outfeedProductIds: string[]; // Subset of pocket products that are "outfeed"
  showSummedOutfeed: boolean; // Whether to show summed outfeed
  showIndividualOutfeed: boolean; // Whether to show individual outfeed products
  individualOutfeedProductIds: string[]; // Which individual outfeed products to display
  summedOutfeedName: string; // Name to display when outfeed is summed
  showOutfeedPph: boolean; // Whether to display outfeed PPH
  outfeedPphDisplayName: string; // Display name for outfeed PPH
  showOutfeedPpm: boolean; // Whether to display outfeed PPM
  outfeedPpmDisplayName: string; // Display name for outfeed PPM
  // Yield Configuration
  showTotalYield: boolean; // Whether to display total yield
  totalYieldDisplayName: string; // Display name for total yield
  showIndividualYields: boolean; // Whether to display individual product yields
  individualYieldProductIds: string[]; // Product IDs to show individual yields for
  // Column Configuration
  columnConfiguration: ColumnConfiguration[]; // Ordered list of columns with settings
  // Dashboard Metrics Configuration
  dashboardMetrics: Array<{
    id: string;
    enabled: boolean;
    type: 'totalOutfeedWeight' | 'avgEmployeeOutfeed' | 'avgEmployeeYield' | 'custom';
    displayName: string;
    icon: string; // Icon identifier
    iconBgColor: string; // Background color for icon
    iconColor: string; // Icon color
    defaultValue?: string; // For demo/placeholder values
  }>;
}

interface GroupFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (groupData: Omit<ProductGroup, 'id'> | ProductGroup, isNew: boolean) => void;
  initialData?: ProductGroup | null;
  title: string;
  existingGroupIds: string[];
  allProducts: Product[];
}

interface ColumnConfigurationSectionProps {
  formData: GroupFormData;
  allProducts?: Product[];
  onColumnOrderChange: (configuration: ColumnConfiguration[]) => void;
}

const ColumnConfigurationSection: React.FC<ColumnConfigurationSectionProps> = ({ 
  formData, 
  allProducts, 
  onColumnOrderChange 
}) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Generate current column configuration based on form data
  const generateCurrentConfiguration = (): ColumnConfiguration[] => {
    const columns: ColumnConfiguration[] = [];
    let order = 0;

    // Essential columns that should always be present
    // Station column
    columns.push({
      id: 'station',
      type: ColumnType.STATION,
      displayName: 'Station',
      enabled: true,
      order: order++
    });

    // Employee Name column
    columns.push({
      id: 'employee-name',
      type: ColumnType.EMPLOYEE_NAME,
      displayName: 'Employee',
      enabled: true,
      order: order++
    });

    // Infeed Weight (always present instead of Infeed Name)
    columns.push({
      id: 'infeed-weight',
      type: ColumnType.INFEED_WEIGHT,
      displayName: formData.infeedDisplayName || 'Infeed',
      enabled: true,
      order: order++
    });

    // Infeed PPH
    if (formData.showInfeedPph) {
      columns.push({
        id: 'infeed-pph',
        type: ColumnType.INFEED_PPH,
        displayName: formData.infeedPphDisplayName || 'PPH',
        enabled: true,
        order: order++
      });
    }

    // Infeed PPM
    if (formData.showInfeedPpm) {
      columns.push({
        id: 'infeed-ppm',
        type: ColumnType.INFEED_PPM,
        displayName: formData.infeedPpmDisplayName || 'PPM',
        enabled: true,
        order: order++
      });
    }

    // Outfeed Columns
    // Show summed outfeed if enabled
    if (formData.showSummedOutfeed && formData.summedOutfeedName) {
      columns.push({
        id: 'outfeed-summed',
        type: ColumnType.OUTFEED_SUMMED,
        displayName: formData.summedOutfeedName,
        enabled: true,
        order: order++
      });
    }

    // Show individual outfeed products if enabled
    if (formData.showIndividualOutfeed && formData.individualOutfeedProductIds && formData.individualOutfeedProductIds.length > 0) {
      formData.individualOutfeedProductIds.forEach(productId => {
        const product = allProducts?.find(p => p.id === productId);
        columns.push({
          id: `outfeed-${productId}`,
          type: ColumnType.OUTFEED_INDIVIDUAL,
          displayName: product?.outfeedDisplayName || product?.name || 'Product',
          enabled: true,
          order: order++,
          productId: productId
        });
      });
    }

    // Outfeed PPH
    if (formData.showOutfeedPph) {
      columns.push({
        id: 'outfeed-pph',
        type: ColumnType.OUTFEED_PPH,
        displayName: formData.outfeedPphDisplayName || 'PPH',
        enabled: true,
        order: order++
      });
    }

    // Outfeed PPM
    if (formData.showOutfeedPpm) {
      columns.push({
        id: 'outfeed-ppm',
        type: ColumnType.OUTFEED_PPM,
        displayName: formData.outfeedPpmDisplayName || 'PPM',
        enabled: true,
        order: order++
      });
    }

    // Total Yield
    if (formData.showTotalYield) {
      columns.push({
        id: 'total-yield',
        type: ColumnType.TOTAL_YIELD,
        displayName: formData.totalYieldDisplayName || 'Total Yield',
        enabled: true,
        order: order++
      });
    }

    // Individual Yields
    if (formData.showIndividualYields && formData.individualYieldProductIds && formData.individualYieldProductIds.length > 0) {
      formData.individualYieldProductIds.forEach(productId => {
        const product = allProducts?.find(p => p.id === productId);
        columns.push({
          id: `yield-${productId}`,
          type: ColumnType.INDIVIDUAL_YIELD,
          displayName: `${product?.outfeedDisplayName || product?.name || 'Product'} Yield`,
          enabled: true,
          order: order++,
          productId: productId
        });
      });
    }

    // Time Worked column (always present)
    columns.push({
      id: 'time-worked',
      type: ColumnType.TIME_WORKED,
      displayName: 'Time Worked',
      enabled: true,
      order: order++
    });

    // Status column (always present)
    columns.push({
      id: 'status',
      type: ColumnType.STATUS,
      displayName: 'Status',
      enabled: true,
      order: order++
    });

    return columns;
  };

  const [currentConfiguration, setCurrentConfiguration] = useState<ColumnConfiguration[]>(() => {
    return formData.columnConfiguration.length > 0 ? formData.columnConfiguration : generateCurrentConfiguration();
  });

  // Update configuration when form data changes, but preserve saved order if it exists
  React.useEffect(() => {
    // Always generate fresh configuration based on current selections
    const newConfig = generateCurrentConfiguration();

    // If we have saved configuration, only use it to preserve order and enabled state
    if (formData.columnConfiguration && formData.columnConfiguration.length > 0) {
      const savedConfig = formData.columnConfiguration;

      // Create a set of valid column IDs from the new configuration
      const validColumnIds = new Set(newConfig.map(col => col.id));

      // Create a map of saved columns for quick lookup of order and enabled state
      // Only include columns that still exist in the new configuration
      const savedConfigMap = new Map(
        savedConfig
          .filter(col => validColumnIds.has(col.id))  // Filter out columns that no longer exist
          .map(col => [col.id, { order: col.order, enabled: col.enabled }])
      );

      // Apply saved order and enabled state to new configuration
      const mergedConfig = newConfig.map((col, index) => {
        const saved = savedConfigMap.get(col.id);
        if (saved) {
          // Use saved order and enabled state
          return {
            ...col,
            order: saved.order,
            enabled: saved.enabled
          };
        } else {
          // New column, add at the end
          return {
            ...col,
            order: 1000 + index, // High number to ensure it goes to the end
            enabled: col.enabled
          };
        }
      });

      // Sort by order
      mergedConfig.sort((a, b) => a.order - b.order);

      // Re-number the orders to be sequential
      mergedConfig.forEach((col, index) => {
        col.order = index;
      });

      setCurrentConfiguration(mergedConfig);
      onColumnOrderChange(mergedConfig);
    } else {
      // No saved configuration, use fresh config as-is
      setCurrentConfiguration(newConfig);
      onColumnOrderChange(newConfig);
    }
  }, [
    formData.infeedDisplayName, formData.showInfeedPph, formData.infeedPphDisplayName,
    formData.showInfeedPpm, formData.infeedPpmDisplayName, formData.showSummedOutfeed,
    formData.showIndividualOutfeed, formData.individualOutfeedProductIds, formData.summedOutfeedName, formData.outfeedProductIds, formData.showOutfeedPph,
    formData.outfeedPphDisplayName, formData.showOutfeedPpm, formData.outfeedPpmDisplayName,
    formData.showTotalYield, formData.totalYieldDisplayName, formData.showIndividualYields,
    formData.individualYieldProductIds
  ]);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', `${index}`);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newConfiguration = [...currentConfiguration];
    const draggedItem = newConfiguration[draggedIndex];
    
    // Remove dragged item
    newConfiguration.splice(draggedIndex, 1);
    
    // Insert at new position
    newConfiguration.splice(dropIndex, 0, draggedItem);
    
    // Update order property
    newConfiguration.forEach((item, index) => {
      item.order = index;
    });
    
    setCurrentConfiguration(newConfiguration);
    onColumnOrderChange(newConfiguration);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const getColumnTypeColor = (type: ColumnType | undefined): string => {
    if (!type) return 'slate';
    switch (type) {
      case ColumnType.INFEED_NAME:
      case ColumnType.INFEED_PPH:
      case ColumnType.INFEED_PPM:
        return 'blue';
      case ColumnType.OUTFEED_SUMMED:
      case ColumnType.OUTFEED_INDIVIDUAL:
      case ColumnType.OUTFEED_PPH:
      case ColumnType.OUTFEED_PPM:
        return 'green';
      case ColumnType.TOTAL_YIELD:
      case ColumnType.INDIVIDUAL_YIELD:
        return 'yellow';
      default:
        return 'slate';
    }
  };

  const getColumnTypeIcon = (type: ColumnType | undefined): string => {
    if (!type) return '📋';
    switch (type) {
      case ColumnType.INFEED_NAME:
        return '📥';
      case ColumnType.INFEED_PPH:
      case ColumnType.OUTFEED_PPH:
        return '⚡';
      case ColumnType.INFEED_PPM:
      case ColumnType.OUTFEED_PPM:
        return '🔄';
      case ColumnType.OUTFEED_SUMMED:
      case ColumnType.OUTFEED_INDIVIDUAL:
        return '📤';
      case ColumnType.TOTAL_YIELD:
      case ColumnType.INDIVIDUAL_YIELD:
        return '📊';
      default:
        return '📋';
    }
  };

  if (currentConfiguration.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">📊</div>
        <p className="text-lg font-medium text-slate-700 mb-2">No Columns Configured</p>
        <p className="text-sm text-slate-500">Configure columns in previous tabs to see them here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm font-medium text-slate-700">
          Column Order ({currentConfiguration.length} columns)
        </div>
        <div className="text-xs text-slate-500">
          💡 Drag and drop to reorder columns
        </div>
      </div>
      
      {currentConfiguration.map((column, index) => {
        const color = getColumnTypeColor(column.type);
        const icon = getColumnTypeIcon(column.type);
        const isDragging = draggedIndex === index;
        const isDragOver = dragOverIndex === index;
        
        return (
          <div
            key={column.id}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            className={`
              bg-${color}-50 rounded-lg p-4 border border-${color}-200 cursor-move transition-all duration-200
              ${isDragging ? 'opacity-50 scale-95' : ''}
              ${isDragOver ? 'ring-2 ring-indigo-500 ring-offset-2' : ''}
              hover:shadow-md hover:scale-[1.02]
            `}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <div className={`w-8 h-8 bg-${color}-500 text-white text-sm rounded flex items-center justify-center font-semibold`}>
                    {index + 1}
                  </div>
                  <div className="text-lg">{icon}</div>
                  <div>
                    <div className="font-medium text-slate-900">{column.displayName}</div>
                    <div className="text-xs text-slate-500">{column.type ? column.type.replace('_', ' ').toLowerCase() : 'unknown type'}</div>
                  </div>
                </div>
                {column.productId && (
                  <span className="text-xs text-slate-500">
                    ({allProducts?.find(p => p.id === column.productId)?.productId || column.productId})
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-400">
                ⋮⋮ Drag to reorder
              </div>
            </div>
          </div>
        );
      })}
      
      <div className="mt-6 p-4 bg-slate-50 rounded-lg">
        <p className="text-sm text-slate-600">
          <strong>💡 Tip:</strong> Drag column cards up or down to reorder them. The order here will be reflected in your reports and dashboards.
        </p>
      </div>
    </div>
  );
};

const GroupFormModal: React.FC<GroupFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  title,
  existingGroupIds,
  allProducts,
}) => {
  const { apiUrl } = useDatabaseContext();
  const { weightUnit } = usePreferences();
  const [activeTab, setActiveTab] = useState(0);
  const [activeBasicSubTab, setActiveBasicSubTab] = useState(0);
  const [activeOutfeedSubTab, setActiveOutfeedSubTab] = useState(0);
  const [activeYieldSubTab, setActiveYieldSubTab] = useState(0);
  const [activeInfeedSubTab, setActiveInfeedSubTab] = useState(0);
  const [systemDefaults, setSystemDefaults] = useState({ defaultInfeedTargetWeight: 10.0 });
  const [formData, setFormData] = useState<GroupFormData>({
    groupId: '',
    groupName: '',
    showOnDashboard: true,
    showOnReports: true,
    infeedDisplayName: '',
    infeedProductId: '', // Single product ID
    pocketAssignments: [], // Array of pocket assignments
    infeedBatchWeight: '', // Batch weight in LBS
    showInfeedPph: false, // PPH display option
    infeedPphDisplayName: 'PPH', // PPH display name
    showInfeedPpm: false, // PPM display option
    infeedPpmDisplayName: 'PPM', // PPM display name
    outfeedProductIds: [], // Outfeed products
    showSummedOutfeed: false, // Show summed outfeed
    showIndividualOutfeed: true, // Show individual outfeed
    individualOutfeedProductIds: [], // Which individual outfeed products to display
    summedOutfeedName: '', // Summed outfeed name
    showOutfeedPph: false, // Outfeed PPH display option
    outfeedPphDisplayName: 'PPH', // Outfeed PPH display name
    showOutfeedPpm: false, // Outfeed PPM display option
    outfeedPpmDisplayName: 'PPM', // Outfeed PPM display name
    // Yield Configuration
    showTotalYield: false, // Total yield display option
    totalYieldDisplayName: 'Total Yield', // Total yield display name
    showIndividualYields: false, // Individual yields display option
    individualYieldProductIds: [], // Product IDs for individual yields
    // Column Configuration
    columnConfiguration: [], // Ordered list of columns with settings
    // Dashboard Metrics Configuration
    dashboardMetrics: [
      {
        id: 'metric-1',
        enabled: true,
        type: 'totalOutfeedWeight' as const,
        displayName: 'Total Outfeed Weight',
        icon: 'scale',
        iconBgColor: 'bg-green-100',
        iconColor: 'text-green-600',
        defaultValue: '',
      },
      {
        id: 'metric-2',
        enabled: true,
        type: 'avgEmployeeOutfeed' as const,
        displayName: 'Average Employee Outfeed',
        icon: 'users',
        iconBgColor: 'bg-sky-100',
        iconColor: 'text-sky-600',
        defaultValue: '',
      },
      {
        id: 'metric-3',
        enabled: true,
        type: 'avgEmployeeYield' as const,
        displayName: 'Average Employee Yield',
        icon: 'chart',
        iconBgColor: 'bg-fuchsia-100',
        iconColor: 'text-fuchsia-600',
        defaultValue: '',
      },
    ],
  });
  const [errors, setErrors] = useState({
    groupId: '',
    groupName: '',
    showOnDashboard: '',
    showOnReports: '',
    infeedDisplayName: '',
    infeedProductId: '',
    pocketAssignments: '',
    infeedBatchWeight: '',
    showInfeedPph: '',
    infeedPphDisplayName: '',
    showInfeedPpm: '',
    infeedPpmDisplayName: '',
    outfeedProductIds: '',
    showSummedOutfeed: '',
    showIndividualOutfeed: '',
    individualOutfeedProductIds: '',
    summedOutfeedName: '',
    showOutfeedPph: '',
    outfeedPphDisplayName: '',
    showOutfeedPpm: '',
    outfeedPpmDisplayName: '',
  });

  useEffect(() => {
    console.log('[GroupFormModal] useEffect triggered, initialData:', initialData);
    if (initialData) {
      console.log('GroupFormModal: Loading initial data:', initialData);
      console.log('GroupFormModal: outfeedProductIds from initialData:', initialData.outfeedProductIds);
      const formDataToSet = {
        groupId: initialData.groupId,
        groupName: initialData.groupName,
        showOnDashboard: initialData.showOnDashboard !== undefined ? initialData.showOnDashboard : true,
        showOnReports: initialData.showOnReports !== undefined ? initialData.showOnReports : true,
        infeedDisplayName: initialData.infeedDisplayName || '',
        infeedProductId: initialData.infeedProductId || '', // Single product ID
        // Deep copy pocket assignments to avoid reference issues
        pocketAssignments: initialData.pocketAssignments 
          ? initialData.pocketAssignments.map(pa => ({ ...pa }))
          : [],
        infeedBatchWeight: initialData.infeedBatchWeight || '',
        showInfeedPph: initialData.showInfeedPph || false,
        infeedPphDisplayName: initialData.infeedPphDisplayName || 'PPH',
        showInfeedPpm: initialData.showInfeedPpm || false,
        infeedPpmDisplayName: initialData.infeedPpmDisplayName || 'PPM',
        // Deep copy arrays to avoid reference issues
        outfeedProductIds: initialData.outfeedProductIds ? [...initialData.outfeedProductIds] : [],
        showSummedOutfeed: initialData.outfeedDisplayMode === 'summed' || initialData.showSummedOutfeed || false,
        showIndividualOutfeed: initialData.outfeedDisplayMode === 'individual' || initialData.showIndividualOutfeed || false,
        individualOutfeedProductIds: initialData.individualOutfeedProductIds 
          ? [...initialData.individualOutfeedProductIds]
          : (initialData.outfeedProductIds ? [...initialData.outfeedProductIds] : []),
        summedOutfeedName: initialData.summedOutfeedName || '',
        showOutfeedPph: initialData.showOutfeedPph || false,
        outfeedPphDisplayName: initialData.outfeedPphDisplayName || 'PPH',
        showOutfeedPpm: initialData.showOutfeedPpm || false,
        outfeedPpmDisplayName: initialData.outfeedPpmDisplayName || 'PPM',
        // Yield Configuration
        showTotalYield: initialData.showTotalYield || false,
        totalYieldDisplayName: initialData.totalYieldDisplayName || 'Total Yield',
        showIndividualYields: initialData.showIndividualYields || false,
        individualYieldProductIds: initialData.individualYieldProductIds || [],
        // Column Configuration
        columnConfiguration: initialData.columnConfiguration || [],
        // Dashboard Metrics Configuration
        dashboardMetrics: (() => {
          console.log('[GroupFormModal] Initial dashboardMetrics:', initialData.dashboardMetrics);
          console.log('[GroupFormModal] Is array?', Array.isArray(initialData.dashboardMetrics));

          // If it's already an array, use it directly
          if (Array.isArray(initialData.dashboardMetrics)) {
            console.log('[GroupFormModal] Using array format directly, length:', initialData.dashboardMetrics.length);
            return initialData.dashboardMetrics;
          }

          // If no dashboardMetrics or not an array, use defaults
          console.log('[GroupFormModal] No valid dashboardMetrics, using defaults');
          return [
            {
              id: 'metric-1',
              enabled: true,
              type: 'totalOutfeedWeight' as const,
              displayName: 'Total Outfeed Weight',
              icon: 'scale',
              iconBgColor: 'bg-green-100',
              iconColor: 'text-green-600',
              defaultValue: '',
            },
            {
              id: 'metric-2',
              enabled: true,
              type: 'avgEmployeeOutfeed' as const,
              displayName: 'Average Employee Outfeed',
              icon: 'users',
              iconBgColor: 'bg-sky-100',
              iconColor: 'text-sky-600',
              defaultValue: '',
            },
            {
              id: 'metric-3',
              enabled: true,
              type: 'avgEmployeeYield' as const,
              displayName: 'Average Employee Yield',
              icon: 'chart',
              iconBgColor: 'bg-fuchsia-100',
              iconColor: 'text-fuchsia-600',
              defaultValue: '',
            },
          ];
        })(),
      };
      console.log('GroupFormModal: Setting form data, outfeedProductIds:', formDataToSet.outfeedProductIds);
      setFormData(formDataToSet);
    } else {
      setFormData({
        groupId: '', groupName: '', showOnDashboard: true, showOnReports: true, infeedDisplayName: 'Infeed',
        infeedProductId: '', // Single product ID
        pocketAssignments: [],
        infeedBatchWeight: systemDefaults.defaultInfeedTargetWeight,
        showInfeedPph: false,
        infeedPphDisplayName: 'PPH',
        showInfeedPpm: false,
        infeedPpmDisplayName: 'PPM',
        outfeedProductIds: [],
        showSummedOutfeed: true,
        showIndividualOutfeed: false,
        individualOutfeedProductIds: [],
        summedOutfeedName: 'Outfeed',
        showOutfeedPph: false,
        outfeedPphDisplayName: 'PPH',
        showOutfeedPpm: false,
        outfeedPpmDisplayName: 'PPM',
        // Yield Configuration
        showTotalYield: true,
        totalYieldDisplayName: 'Yield',
        showIndividualYields: false,
        individualYieldProductIds: [],
        // Column Configuration
        columnConfiguration: [],
        // Dashboard Metrics Configuration
        dashboardMetrics: [
          {
            id: 'metric-1',
            enabled: true,
            type: 'totalOutfeedWeight' as const,
            displayName: 'Total Outfeed Weight',
            icon: 'scale',
            iconBgColor: 'bg-green-100',
            iconColor: 'text-green-600',
            defaultValue: '',
          },
          {
            id: 'metric-2',
            enabled: true,
            type: 'avgEmployeeOutfeed' as const,
            displayName: 'Average Employee Outfeed',
            icon: 'users',
            iconBgColor: 'bg-sky-100',
            iconColor: 'text-sky-600',
            defaultValue: '',
          },
          {
            id: 'metric-3',
            enabled: true,
            type: 'avgEmployeeYield' as const,
            displayName: 'Average Employee Yield',
            icon: 'chart',
            iconBgColor: 'bg-fuchsia-100',
            iconColor: 'text-fuchsia-600',
            defaultValue: '',
          },
        ],
      });
    }
    setErrors({ 
        groupId: '', groupName: '', infeedDisplayName: '', 
        infeedProductId: '', pocketAssignments: '', infeedBatchWeight: '',
        showInfeedPph: '', infeedPphDisplayName: '',
        showInfeedPpm: '', infeedPpmDisplayName: '',
        outfeedProductIds: '', outfeedDisplayMode: '', summedOutfeedName: '',
        showOutfeedPph: '', outfeedPphDisplayName: '',
        showOutfeedPpm: '', outfeedPpmDisplayName: ''
    });
  }, [isOpen, initialData, systemDefaults]);

  // Fetch system defaults when modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchDefaults = async () => {
        try {
          const response = await fetch(`${apiUrl}/system-defaults`);
          if (response.ok) {
            const data = await response.json();
            const defaults = data.data;
            setSystemDefaults({
              defaultInfeedTargetWeight: defaults.defaultInfeedTargetWeight || 10.0,
            });
          }
        } catch (error) {
          console.error('Error fetching system defaults:', error);
        }
      };
      fetchDefaults();
    }
  }, [isOpen, apiUrl]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'infeedBatchWeight'
        ? (value === '' ? '' : parseFloat(value))
        : value,
    }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleInfeedProductChange = (selectedId: string) => {
    setFormData(prev => ({ ...prev, infeedProductId: selectedId }));
    setErrors(prev => ({ ...prev, infeedProductId: '' }));
  };

  const handlePocketAssignmentsChange = (newPocketAssignments: PocketAssignment[]) => {
    setFormData(prev => ({ 
      ...prev, 
      pocketAssignments: newPocketAssignments,
      // Reset outfeed selection when pocket assignments change
      outfeedProductIds: prev.outfeedProductIds.filter(id => 
        newPocketAssignments.some(assignment => assignment.productId === id)
      )
    }));
    setErrors(prev => ({ ...prev, pocketAssignments: '', outfeedProductIds: '' }));
  };

  const handleOutfeedProductSelection = (productId: string, isSelected: boolean) => {
    setFormData(prev => {
      if (isSelected) {
        // Adding to outfeed products
        return {
          ...prev,
          outfeedProductIds: [...prev.outfeedProductIds, productId]
        };
      } else {
        // Removing from outfeed products - also remove from individual display arrays
        return {
          ...prev,
          outfeedProductIds: prev.outfeedProductIds.filter(id => id !== productId),
          // Also remove from individual outfeed display if it was there
          individualOutfeedProductIds: prev.individualOutfeedProductIds.filter(id => id !== productId),
          // Also remove from individual yield display if it was there
          individualYieldProductIds: prev.individualYieldProductIds.filter(id => id !== productId)
        };
      }
    });
    setErrors(prev => ({ ...prev, outfeedProductIds: '' }));
  };

  const handleOutfeedDisplayModeChange = (mode: 'summed' | 'individual') => {
    setFormData(prev => ({ ...prev, outfeedDisplayMode: mode }));
    setErrors(prev => ({ ...prev, outfeedDisplayMode: '' }));
  };

  const validate = (): boolean => {
    let isValid = true;
    const newErrors = { 
        groupId: '', groupName: '', infeedDisplayName: '', 
        infeedProductId: '', pocketAssignments: '', infeedBatchWeight: '',
        showInfeedPph: '', infeedPphDisplayName: '',
        showInfeedPpm: '', infeedPpmDisplayName: '',
        outfeedProductIds: '', outfeedDisplayMode: '', summedOutfeedName: '',
        showOutfeedPph: '', outfeedPphDisplayName: '',
        showOutfeedPpm: '', outfeedPpmDisplayName: ''
    };

    console.log('Starting validation...');
    console.log('Form data to validate:', {
      groupId: formData.groupId,
      groupName: formData.groupName,
      infeedDisplayName: formData.infeedDisplayName,
      infeedProductId: formData.infeedProductId,
      pocketAssignments: formData.pocketAssignments,
      outfeedProductIds: formData.outfeedProductIds,
      showSummedOutfeed: formData.showSummedOutfeed,
      summedOutfeedName: formData.summedOutfeedName
    });

    if (!formData.groupId.trim()) {
      newErrors.groupId = 'Group ID is required.';
      console.log('Validation error: Group ID is required');
      isValid = false;
    } else if (!/^[a-zA-Z0-9-_]+$/.test(formData.groupId.trim())) {
      newErrors.groupId = 'Group ID must be alphanumeric (can include - and _).';
      console.log('Validation error: Group ID format invalid');
      isValid = false;
    } else {
      const isGroupIdTaken = existingGroupIds && existingGroupIds.some(id =>
        id.toLowerCase() === formData.groupId.trim().toLowerCase() &&
        (!initialData || initialData.groupId.toLowerCase() !== formData.groupId.trim().toLowerCase())
      );
      if (isGroupIdTaken) {
        newErrors.groupId = 'This Group ID is already taken.';
        console.log('Validation error: Group ID already taken');
        isValid = false;
      }
    }

    if (!formData.groupName.trim()) {
      newErrors.groupName = 'Group Name is required.';
      console.log('Validation error: Group Name is required');
      isValid = false;
    }
    if (!formData.infeedDisplayName.trim()) {
      newErrors.infeedDisplayName = 'Infeed Display Name is required.';
      console.log('Validation error: Infeed Display Name is required');
      isValid = false;
    }

    // Validate PPH display name when PPH is enabled
    if (formData.showInfeedPph && !formData.infeedPphDisplayName.trim()) {
      newErrors.infeedPphDisplayName = 'PPH Display Name is required when PPH is enabled.';
      console.log('Validation error: Infeed PPH Display Name is required');
      isValid = false;
    }

    // Validate PPM display name when PPM is enabled
    if (formData.showInfeedPpm && !formData.infeedPpmDisplayName.trim()) {
      newErrors.infeedPpmDisplayName = 'PPM Display Name is required when PPM is enabled.';
      console.log('Validation error: Infeed PPM Display Name is required');
      isValid = false;
    }

    // Validate outfeed PPH display name when outfeed PPH is enabled
    if (formData.showOutfeedPph && !formData.outfeedPphDisplayName.trim()) {
      newErrors.outfeedPphDisplayName = 'Outfeed PPH Display Name is required when outfeed PPH is enabled.';
      console.log('Validation error: Outfeed PPH Display Name is required');
      isValid = false;
    }

    // Validate outfeed PPM display name when outfeed PPM is enabled
    if (formData.showOutfeedPpm && !formData.outfeedPpmDisplayName.trim()) {
      newErrors.outfeedPpmDisplayName = 'Outfeed PPM Display Name is required when outfeed PPM is enabled.';
      console.log('Validation error: Outfeed PPM Display Name is required');
      isValid = false;
    }

    // Mutual exclusivity validation for PPH
    if (formData.showInfeedPph && formData.showOutfeedPph) {
      newErrors.showInfeedPph = 'PPH can only be enabled on either infeed or outfeed, not both.';
      newErrors.showOutfeedPph = 'PPH can only be enabled on either infeed or outfeed, not both.';
      console.log('Validation error: PPH enabled on both infeed and outfeed');
      isValid = false;
    }

    // Mutual exclusivity validation for PPM
    if (formData.showInfeedPpm && formData.showOutfeedPpm) {
      newErrors.showInfeedPpm = 'PPM can only be enabled on either infeed or outfeed, not both.';
      newErrors.showOutfeedPpm = 'PPM can only be enabled on either infeed or outfeed, not both.';
      console.log('Validation error: PPM enabled on both infeed and outfeed');
      isValid = false;
    }

    // Validate infeed product (exactly 1 required)
    if (!formData.infeedProductId) {
      newErrors.infeedProductId = 'Exactly 1 infeed product is required.';
      console.log('Validation error: Infeed product is required');
      isValid = false;
    }

    // Validate pocket assignments - Required for NEW groups, optional for existing ones being updated
    // This allows existing groups without pockets to be updated for other fields
    const isNewGroup = !initialData?.id;
    
    if (isNewGroup) {
      // For new groups, require at least one pocket with a product
      if (formData.pocketAssignments.length === 0) {
        newErrors.pocketAssignments = 'At least 1 pocket assignment is required for new product groups.';
        console.log('Validation error: No pocket assignments for new group');
        isValid = false;
      } else if (formData.pocketAssignments.length > 4) {
        newErrors.pocketAssignments = 'Maximum 4 pocket assignments allowed.';
        console.log('Validation error: Too many pocket assignments');
        isValid = false;
      } else {
        // Check that at least one pocket has a product assigned
        const hasAssignedProduct = formData.pocketAssignments.some(pa => pa.productId && pa.productId !== '');
        if (!hasAssignedProduct) {
          newErrors.pocketAssignments = 'At least 1 pocket must have a product assigned for new groups.';
          console.log('Validation error: No products assigned to pockets in new group');
          isValid = false;
        }
      }
    } else {
      // For existing groups, only validate if pockets are provided
      if (formData.pocketAssignments.length > 4) {
        newErrors.pocketAssignments = 'Maximum 4 pocket assignments allowed.';
        console.log('Validation error: Too many pocket assignments');
        isValid = false;
      }
    }

    // Validate infeed batch weight (optional, but if provided must be positive)
    if (formData.infeedBatchWeight !== '' && (isNaN(Number(formData.infeedBatchWeight)) || Number(formData.infeedBatchWeight) <= 0)) {
      newErrors.infeedBatchWeight = 'Infeed Batch Weight must be a positive number.';
      console.log('Validation error: Invalid infeed batch weight');
      isValid = false;
    }

    // Validate outfeed products - Required for new groups with pockets, optional for updates
    if (isNewGroup && formData.pocketAssignments.length > 0 && formData.outfeedProductIds.length === 0) {
      newErrors.outfeedProductIds = 'At least 1 outfeed product must be selected from the assigned pocket products.';
      console.log('Validation error: No outfeed products selected for new group');
      isValid = false;
    } else if (!isNewGroup && formData.pocketAssignments.length > 0 && formData.pocketAssignments.some(pa => pa.productId) && formData.outfeedProductIds.length === 0) {
      // For existing groups, only require outfeed if they have pocket assignments with products
      // This is a warning, not a hard requirement for backward compatibility
      console.log('Warning: No outfeed products selected but pockets have products assigned');
    }

    // Validate summed outfeed name when showing summed display
    if (formData.showSummedOutfeed && formData.outfeedProductIds.length > 0 && !formData.summedOutfeedName.trim()) {
      newErrors.summedOutfeedName = 'Summed Outfeed Name is required when showing summed display.';
      console.log('Validation error: Summed outfeed name required');
      isValid = false;
    }
    
    console.log('Validation complete. Is valid:', isValid);
    console.log('Validation errors:', newErrors);
    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted, validating...');
    console.log('Current formData:', formData);
    console.log('Current errors before validation:', errors);
    if (!validate()) {
      console.log('Validation failed');
      console.log('Errors after validation:', errors);
      return;
    }
    console.log('Validation passed, preparing data...');
    const infeedBatchWeightAsNumber = formData.infeedBatchWeight === '' ? undefined : Number(formData.infeedBatchWeight);

    const finalFormData = {
        ...formData,
        infeedBatchWeight: infeedBatchWeightAsNumber,
        showInfeedPph: formData.showInfeedPph,
        infeedPphDisplayName: formData.infeedPphDisplayName,
        showInfeedPpm: formData.showInfeedPpm,
        infeedPpmDisplayName: formData.infeedPpmDisplayName,
        outfeedProductIds: formData.outfeedProductIds,
        showSummedOutfeed: formData.showSummedOutfeed,
        showIndividualOutfeed: formData.showIndividualOutfeed,
        individualOutfeedProductIds: formData.individualOutfeedProductIds,
        outfeedDisplayMode: formData.showSummedOutfeed ? 'summed' : 'individual', // For backward compatibility
        summedOutfeedName: formData.summedOutfeedName,
        showOutfeedPph: formData.showOutfeedPph,
        outfeedPphDisplayName: formData.outfeedPphDisplayName,
        showOutfeedPpm: formData.showOutfeedPpm,
        outfeedPpmDisplayName: formData.outfeedPpmDisplayName,
        // Include column configuration
        columnConfiguration: formData.columnConfiguration,
    };

    console.log('Calling onSubmit with:', finalFormData);
    if (initialData) {
      onSubmit({ ...initialData, ...finalFormData }, false);
    } else {
      onSubmit(finalFormData, true);
    }
    console.log('onSubmit called successfully');
  };

  // Check if tabs have errors
  const tabHasError = (tabIndex: number) => {
    switch (tabIndex) {
      case 0: // Basic Info
        return !!(errors.groupId || errors.groupName);
      case 1: // Infeed
        return !!(errors.infeedDisplayName || errors.infeedProductId || errors.infeedBatchWeight || errors.showInfeedPph || errors.infeedPphDisplayName || errors.showInfeedPpm || errors.infeedPpmDisplayName);
      case 2: // Pockets
        return !!errors.pocketAssignments;
      case 3: // Outfeed
        return !!(errors.outfeedProductIds || errors.showSummedOutfeed || errors.showIndividualOutfeed || errors.summedOutfeedName || errors.showOutfeedPph || errors.outfeedPphDisplayName || errors.showOutfeedPpm || errors.outfeedPpmDisplayName);
      case 4: // Yield
        return false; // No validation errors for yield tab currently
      case 5: // Summary Metrics
        return false; // No validation errors for summary metrics
      case 6: // Columns (Column Configuration)
        return false; // No validation errors for columns
      default:
        return false;
    }
  };

  const tabs = [
    { name: 'Basic Info', icon: '📋' },
    { name: 'Infeed', icon: '📥' },
    { name: 'Pockets', icon: '🎯' },
    { name: 'Outfeed', icon: '📤' },
    { name: 'Yield', icon: '📈' },
    { name: 'Summary Metrics', icon: '📊' },
    { name: 'Columns', icon: '📐' },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="5xl">
      <form onSubmit={handleSubmit} noValidate className="flex flex-col h-full">
        {/* Tab Navigation - Fixed at top */}
        <div className="border-b border-slate-200 -mx-6 px-6 mb-6 overflow-x-auto flex-shrink-0">
          <nav className="-mb-px flex space-x-4 min-w-max" aria-label="Tabs">
            {tabs.map((tab, index) => (
              <button
                key={tab.name}
                type="button"
                onClick={() => setActiveTab(index)}
                className={`
                  py-3 px-1 border-b-2 font-medium text-sm transition-all duration-200
                  ${activeTab === index
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  }
                  ${tabHasError(index) ? 'relative' : ''}
                `}
              >
                <span className="flex items-center space-x-2">
                  <span className="text-lg">{tab.icon}</span>
                  <span>{tab.name}</span>
                  {tabHasError(index) && (
                    <span className="absolute -top-1 -right-1 h-2 w-2 bg-red-500 rounded-full animate-pulse"></span>
                  )}
                </span>
              </button>
            ))}
          </nav>
        </div>
        
        {/* Tab Content - Scrollable area */}
        <div className="flex-1 overflow-y-auto min-h-0" style={{ maxHeight: 'calc(80vh - 250px)' }}>
          {/* Tab 0: Basic Information */}
          {activeTab === 0 && (
            <div className="animate-fadeIn">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Basic Group Details</h3>
                <p className="text-sm text-slate-600">Configure the fundamental properties of your product group.</p>
              </div>
              
              {/* Sub-tabs for Basic Information */}
              <div className="border-b border-slate-200 mb-6">
                <nav className="-mb-px flex space-x-8" aria-label="Basic info sub-tabs">
                  <button
                    type="button"
                    onClick={() => setActiveBasicSubTab(0)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeBasicSubTab === 0
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                    }`}
                  >
                    Group Details
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveBasicSubTab(1)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeBasicSubTab === 1
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                    }`}
                  >
                    Visibility Settings
                  </button>
                </nav>
              </div>
              
              {/* Sub-tab 0: Group Details */}
              {activeBasicSubTab === 0 && (
                <div className="bg-white rounded-lg border-2 border-slate-200 p-6">
                  <h4 className="text-lg font-semibold text-slate-800 mb-3">📋 Group Identification</h4>
                  <p className="text-sm text-slate-600 mb-4">Set the unique identifiers for this product group.</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                    <InputField 
                      label="Group ID" 
                      name="groupId" 
                      value={formData.groupId} 
                      onChange={handleChange} 
                      error={errors.groupId} 
                      required 
                    />
                    <InputField 
                      label="Group Name" 
                      name="groupName" 
                      value={formData.groupName} 
                      onChange={handleChange} 
                      error={errors.groupName} 
                      required 
                    />
                  </div>
                </div>
              )}
              
              {/* Sub-tab 1: Visibility Settings */}
              {activeBasicSubTab === 1 && (
                <div className="bg-white rounded-lg border-2 border-slate-200 p-6">
                  <h4 className="text-lg font-semibold text-slate-800 mb-3">📍 Visibility Settings</h4>
                  <p className="text-sm text-slate-600 mb-4">Choose where this product group should be available</p>
                  
                  <div className="space-y-3">
                    <label className="flex items-center p-4 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors border-2 border-transparent hover:border-blue-300">
                      <input
                        type="checkbox"
                        checked={formData.showOnDashboard}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, showOnDashboard: e.target.checked }));
                        }}
                        className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded"
                      />
                      <div className="ml-3">
                        <div className="text-sm font-semibold text-slate-900">Show on Dashboard</div>
                        <div className="text-xs text-slate-600">
                          Display this product group on the main dashboard
                        </div>
                      </div>
                    </label>
                    
                    <label className="flex items-center p-4 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors border-2 border-transparent hover:border-green-300">
                      <input
                        type="checkbox"
                        checked={formData.showOnReports}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, showOnReports: e.target.checked }));
                        }}
                        className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded"
                      />
                      <div className="ml-3">
                        <div className="text-sm font-semibold text-slate-900">Show on Reports</div>
                        <div className="text-xs text-slate-600">
                          Include this product group in reports
                        </div>
                      </div>
                    </label>
                  </div>
                  
                  {!formData.showOnDashboard && !formData.showOnReports && (
                    <p className="mt-3 text-xs text-amber-600">
                      ⚠️ This product group won't be visible anywhere. Please select at least one visibility option.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Tab 1: Infeed Configuration */}
          {activeTab === 1 && (
            <div className="animate-fadeIn">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Infeed Configuration</h3>
                <p className="text-sm text-slate-600">Configure the infeed product and display settings.</p>
              </div>
              
              {/* Sub-tabs for Infeed Configuration */}
              <div className="border-b border-slate-200 mb-6">
                <nav className="-mb-px flex space-x-8" aria-label="Infeed sub-tabs">
                  <button
                    type="button"
                    onClick={() => setActiveInfeedSubTab(0)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeInfeedSubTab === 0
                        ? 'border-green-500 text-green-600'
                        : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                    }`}
                  >
                    Basic Settings
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveInfeedSubTab(1)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeInfeedSubTab === 1
                        ? 'border-green-500 text-green-600'
                        : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                    }`}
                  >
                    Metrics (PPH/PPM)
                  </button>
                </nav>
              </div>
              
              {/* Sub-tab 0: Basic Settings */}
              {activeInfeedSubTab === 0 && (
                <div className="bg-white rounded-lg border-2 border-slate-200 p-6">
                  <h4 className="text-lg font-semibold text-slate-800 mb-3">📥 Basic Infeed Settings</h4>
                  <p className="text-sm text-slate-600 mb-4">Configure the fundamental infeed properties.</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 mb-6">
                    <InputField 
                      label="Infeed Display Name" 
                      name="infeedDisplayName" 
                      value={formData.infeedDisplayName} 
                      onChange={handleChange} 
                      error={errors.infeedDisplayName} 
                      required 
                    />
                    {/* Custom field for batch weight with unit in input */}
                    <div className="mb-5">
                      <label htmlFor="infeedBatchWeight" className="block text-sm font-semibold text-slate-700 mb-2">
                        Infeed Batch Weight
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          id="infeedBatchWeight"
                          name="infeedBatchWeight"
                          value={formData.infeedBatchWeight === '' ? '' : formData.infeedBatchWeight.toString()}
                          onChange={handleChange}
                          step="any"
                          min="0"
                          className={`w-full px-4 py-2.5 pr-16 border-2 rounded-lg shadow-sm transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                            errors.infeedBatchWeight ? 'border-red-300 bg-red-50' : 'border-slate-200 hover:border-slate-300 bg-white'
                          }`}
                          aria-describedby={errors.infeedBatchWeight ? 'infeedBatchWeight-error' : undefined}
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-slate-500">
                          {weightUnit === 'KGS' ? 'kg' : 'lbs'}
                        </span>
                      </div>
                      {errors.infeedBatchWeight && <p id="infeedBatchWeight-error" className="mt-1.5 text-xs text-red-600 font-medium">{errors.infeedBatchWeight}</p>}
                    </div>
                  </div>
                  
                  <SingleProductSelectionInput
                      allProducts={allProducts}
                      selectedProductId={formData.infeedProductId}
                      onChange={handleInfeedProductChange}
                      label="Infeed Product"
                      inputId="infeed-product-select"
                      productType={ProductType.INFEED}
                      error={errors.infeedProductId}
                      required
                  />
                </div>
              )}

              {/* Sub-tab 1: Metrics (PPH/PPM) */}
              {activeInfeedSubTab === 1 && (
                <div className="space-y-6">
                  {/* PPH Configuration */}
                  <div className="bg-white rounded-lg border-2 border-slate-200 p-6">
                    <h4 className="text-lg font-semibold text-slate-800 mb-3">📊 Pounds Per Hour (PPH) Display</h4>
                    <p className="text-sm text-slate-600 mb-4">Configure whether to display infeed pounds per hour metrics in reports.</p>
                    
                    <div className="space-y-4">
                      <label className={`flex items-center p-4 rounded-lg cursor-pointer transition-colors border-2 ${
                        formData.showOutfeedPph ? 'bg-gray-100 border-gray-300 cursor-not-allowed' : 'bg-blue-50 hover:bg-blue-100 border-transparent hover:border-blue-300'
                      }`}>
                        <input
                          type="checkbox"
                          checked={formData.showInfeedPph}
                          disabled={formData.showOutfeedPph}
                          onChange={(e) => {
                            if (!formData.showOutfeedPph) {
                              setFormData(prev => ({ 
                                ...prev, 
                                showInfeedPph: e.target.checked,
                                infeedPphDisplayName: e.target.checked ? prev.infeedPphDisplayName || 'PPH' : prev.infeedPphDisplayName
                              }));
                              setErrors(prev => ({ ...prev, showInfeedPph: '', infeedPphDisplayName: '' }));
                            }
                          }}
                          className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded disabled:cursor-not-allowed disabled:opacity-50"
                        />
                        <div className="ml-3">
                          <div className="text-sm font-semibold text-slate-900">Display Infeed Pounds Per Hour</div>
                          <div className="text-xs text-slate-600">
                            {formData.showOutfeedPph 
                              ? "PPH is already enabled for outfeed. Disable outfeed PPH to enable infeed PPH."
                              : "Show PPH metrics for this infeed in reports and dashboards"
                            }
                          </div>
                        </div>
                      </label>

                      {errors.showInfeedPph && (
                        <p className="text-xs text-red-600 font-medium">{errors.showInfeedPph}</p>
                      )}

                      {/* PPH Display Name Input */}
                      {formData.showInfeedPph && (
                        <div className="pt-4 border-t border-slate-200">
                          <InputField 
                            label="PPH Display Name" 
                            name="infeedPphDisplayName" 
                            value={formData.infeedPphDisplayName} 
                            onChange={handleChange} 
                            error={errors.infeedPphDisplayName} 
                            required 
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            This name will appear in reports when displaying pounds per hour metrics
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* PPM Configuration */}
                  <div className="bg-white rounded-lg border-2 border-slate-200 p-6">
                    <h4 className="text-lg font-semibold text-slate-800 mb-3">⏱️ Pieces Per Minute (PPM) Display</h4>
                    <p className="text-sm text-slate-600 mb-4">Configure whether to display infeed pieces per minute metrics in reports.</p>
                    
                    <div className="space-y-4">
                      <label className={`flex items-center p-4 rounded-lg cursor-pointer transition-colors border-2 ${
                        formData.showOutfeedPpm ? 'bg-gray-100 border-gray-300 cursor-not-allowed' : 'bg-green-50 hover:bg-green-100 border-transparent hover:border-green-300'
                      }`}>
                        <input
                          type="checkbox"
                          checked={formData.showInfeedPpm}
                          disabled={formData.showOutfeedPpm}
                          onChange={(e) => {
                            if (!formData.showOutfeedPpm) {
                              setFormData(prev => ({ 
                                ...prev, 
                                showInfeedPpm: e.target.checked,
                                infeedPpmDisplayName: e.target.checked ? prev.infeedPpmDisplayName || 'PPM' : prev.infeedPpmDisplayName
                              }));
                              setErrors(prev => ({ ...prev, showInfeedPpm: '', infeedPpmDisplayName: '' }));
                            }
                          }}
                          className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded disabled:cursor-not-allowed disabled:opacity-50"
                        />
                        <div className="ml-3">
                          <div className="text-sm font-semibold text-slate-900">Display Infeed Pieces Per Minute</div>
                          <div className="text-xs text-slate-600">
                            {formData.showOutfeedPpm 
                              ? "PPM is already enabled for outfeed. Disable outfeed PPM to enable infeed PPM."
                              : "Show PPM metrics for this infeed in reports and dashboards"
                            }
                          </div>
                        </div>
                      </label>

                      {errors.showInfeedPpm && (
                        <p className="text-xs text-red-600 font-medium">{errors.showInfeedPpm}</p>
                      )}

                      {/* PPM Display Name Input */}
                      {formData.showInfeedPpm && (
                        <div className="pt-4 border-t border-slate-200">
                          <InputField 
                            label="PPM Display Name" 
                            name="infeedPpmDisplayName" 
                            value={formData.infeedPpmDisplayName} 
                            onChange={handleChange} 
                            error={errors.infeedPpmDisplayName} 
                            required 
                          />
                          <p className="text-xs text-slate-500 mt-1">
                            This name will appear in reports when displaying pieces per minute metrics
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tab 2: Pocket Assignments */}
          {activeTab === 2 && (
            <div className="animate-fadeIn">
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Pocket Product Assignments</h3>
                <p className="text-sm text-slate-600">Assign products to pockets (1-4 pockets available).</p>
              </div>
              
              <PocketAssignmentInput
                  allProducts={allProducts}
                  pocketAssignments={formData.pocketAssignments}
                  onChange={handlePocketAssignmentsChange}
                  error={errors.pocketAssignments}
                  maxPockets={4}
              />
            </div>
          )}

          {/* Tab 3: Outfeed Configuration */}
          {activeTab === 3 && (
            <div className="animate-fadeIn">
              {formData.pocketAssignments.length > 0 ? (
                <div>
                  <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-6 mb-6">
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Outfeed Configuration</h3>
                    <p className="text-sm text-slate-600">Configure how outfeed products are tracked and displayed in reports.</p>
                  </div>
                  
                  {/* Sub-tabs for Outfeed Configuration */}
                  <div className="border-b border-slate-200 mb-6">
                    <nav className="-mb-px flex space-x-8" aria-label="Outfeed sub-tabs">
                      <button
                        type="button"
                        onClick={() => setActiveOutfeedSubTab(0)}
                        className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                          activeOutfeedSubTab === 0
                            ? 'border-orange-500 text-orange-600'
                            : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                        }`}
                      >
                        Product Selection
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveOutfeedSubTab(1)}
                        disabled={formData.outfeedProductIds.length === 0}
                        className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                          formData.outfeedProductIds.length === 0
                            ? 'text-slate-300 cursor-not-allowed'
                            : activeOutfeedSubTab === 1
                            ? 'border-orange-500 text-orange-600'
                            : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                        }`}
                      >
                        Display Options
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveOutfeedSubTab(2)}
                        disabled={formData.outfeedProductIds.length === 0}
                        className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                          formData.outfeedProductIds.length === 0
                            ? 'text-slate-300 cursor-not-allowed'
                            : activeOutfeedSubTab === 2
                            ? 'border-orange-500 text-orange-600'
                            : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                        }`}
                      >
                        Metrics (PPH/PPM)
                      </button>
                    </nav>
                  </div>
                  
                  {/* Sub-tab 0: Product Selection */}
                  {activeOutfeedSubTab === 0 && (
                  <div className="mb-6 bg-white rounded-lg border-2 border-slate-200 p-6">
                    <label className="block text-sm font-semibold text-slate-700 mb-3">
                      Select Outfeed Products <span className="text-red-500">*</span>
                    </label>
                    <p className="text-sm text-slate-500 mb-4">
                      Choose which pocket products should be tracked as outfeed (excluding waste bins)
                    </p>
                    <div className="space-y-3">
                      {formData.pocketAssignments.map((assignment) => {
                        const product = allProducts.find(p => p.id === assignment.productId);
                        if (!product) return null;
                        
                        return (
                          <label key={assignment.pocketNumber} className="flex items-center p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
                            <input
                              type="checkbox"
                              checked={formData.outfeedProductIds.includes(assignment.productId)}
                              onChange={(e) => handleOutfeedProductSelection(assignment.productId, e.target.checked)}
                              className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded"
                            />
                            <div className="ml-3">
                              <div className="text-sm font-semibold text-slate-900">
                                Pocket {assignment.pocketNumber}: {product.name}
                              </div>
                              <div className="text-xs text-slate-500">
                                {product.productId} • {product.pieceWeight} lbs/piece
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    {errors.outfeedProductIds && (
                      <p className="mt-2 text-xs text-red-600 font-medium">{errors.outfeedProductIds}</p>
                    )}
                  </div>
                  )}

                  {/* Sub-tab 1: Display Options */}
                  {activeOutfeedSubTab === 1 && (
                    formData.outfeedProductIds.length > 0 ? (
                    <div className="bg-white rounded-lg border-2 border-slate-200 p-6">
                      <label className="block text-sm font-semibold text-slate-700 mb-3">
                        Display Options
                      </label>
                      <p className="text-sm text-slate-500 mb-4">
                        Choose how to display the selected outfeed products in reports (you can select both)
                      </p>
                      <div className="space-y-3">
                        {/* Show Summed Total Option */}
                        <div className="space-y-3">
                          <label className="flex items-center p-4 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors border-2 border-transparent hover:border-blue-300">
                            <input
                              type="checkbox"
                              checked={formData.showSummedOutfeed}
                              onChange={(e) => {
                                setFormData(prev => ({ ...prev, showSummedOutfeed: e.target.checked }));
                                setErrors(prev => ({ ...prev, summedOutfeedName: '' }));
                              }}
                              className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded"
                            />
                            <div className="ml-3">
                              <div className="text-sm font-semibold text-slate-900">Show Summed Total</div>
                              <div className="text-xs text-slate-600">
                                Display a combined total of all selected outfeed products
                              </div>
                            </div>
                          </label>
                          
                          {/* Summed Outfeed Name Input - Nested under Show Summed Total */}
                          {formData.showSummedOutfeed && (
                            <div className="ml-8 p-4 bg-blue-50/50 rounded-lg border border-blue-200">
                              <label htmlFor="summedOutfeedName" className="block text-sm font-semibold text-slate-700 mb-2">
                                Summed Outfeed Name <span className="text-red-500">*</span>
                              </label>
                              <p className="text-sm text-slate-500 mb-3">
                                Enter the name to display for the combined outfeed products
                              </p>
                              <input
                                type="text"
                                id="summedOutfeedName"
                                name="summedOutfeedName"
                                value={formData.summedOutfeedName}
                                onChange={(e) => {
                                  setFormData(prev => ({ ...prev, summedOutfeedName: e.target.value }));
                                  setErrors(prev => ({ ...prev, summedOutfeedName: '' }));
                                }}
                                className={`w-full px-4 py-2.5 border-2 rounded-lg shadow-sm transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                                  errors.summedOutfeedName ? 'border-red-300 bg-red-50' : 'border-slate-200 hover:border-slate-300 bg-white'
                                }`}
                                placeholder="e.g., Combined Outfeed"
                              />
                              {errors.summedOutfeedName && (
                                <p className="mt-1.5 text-xs text-red-600 font-medium">{errors.summedOutfeedName}</p>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Show Individual Products Option */}
                        <div>
                          <label className="flex items-center p-4 bg-green-50 rounded-lg cursor-pointer hover:bg-green-100 transition-colors border-2 border-transparent hover:border-green-300">
                            <input
                              type="checkbox"
                              checked={formData.showIndividualOutfeed}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setFormData(prev => ({ 
                                  ...prev, 
                                  showIndividualOutfeed: checked,
                                  // Initialize with all outfeed products if empty
                                  individualOutfeedProductIds: checked && prev.individualOutfeedProductIds.length === 0 
                                    ? prev.outfeedProductIds 
                                    : prev.individualOutfeedProductIds
                                }));
                              }}
                              className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded"
                            />
                            <div className="ml-3">
                              <div className="text-sm font-semibold text-slate-900">Show Individual Products</div>
                              <div className="text-xs text-slate-600">
                                Display selected outfeed products as individual columns
                              </div>
                            </div>
                          </label>
                          
                          {/* Individual Product Selection - Nested under Show Individual Products */}
                          {formData.showIndividualOutfeed && formData.pocketAssignments.length > 0 && (
                            <div className="ml-8 p-4 bg-green-50/50 rounded-lg border border-green-200 mt-2">
                              <label className="block text-sm font-semibold text-slate-700 mb-3">
                                Select Products to Display
                              </label>
                              <p className="text-sm text-slate-500 mb-3">
                                Choose which pocket products should appear as individual columns on the dashboard
                              </p>
                              <div className="space-y-2 max-h-48 overflow-y-auto">
                                {/* Get all unique pocket product IDs */}
                                {Array.from(new Set(formData.pocketAssignments.map(pa => pa.productId).filter(id => id))).map(productId => {
                                  const product = allProducts?.find(p => p.id === productId);
                                  if (!product) return null;

                                  // Find which pocket(s) this product is assigned to
                                  const pockets = formData.pocketAssignments
                                    .filter(pa => pa.productId === productId)
                                    .map(pa => pa.pocketNumber);

                                  // Check if this product counts toward yield
                                  const countsTowardYield = formData.outfeedProductIds.includes(productId);

                                  return (
                                    <label key={productId} className="flex items-center p-2 bg-white rounded border border-slate-200 hover:bg-slate-50 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={formData.individualOutfeedProductIds.includes(productId)}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setFormData(prev => ({
                                              ...prev,
                                              individualOutfeedProductIds: [...prev.individualOutfeedProductIds, productId]
                                            }));
                                          } else {
                                            setFormData(prev => ({
                                              ...prev,
                                              individualOutfeedProductIds: prev.individualOutfeedProductIds.filter(id => id !== productId)
                                            }));
                                          }
                                        }}
                                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded"
                                      />
                                      <div className="ml-3 flex-1">
                                        <div className="text-sm font-medium text-slate-900">
                                          {product.outfeedDisplayName || product.name}
                                        </div>
                                        <div className="text-xs text-slate-500">
                                          Pocket{pockets.length > 1 ? 's' : ''}: {pockets.join(', ')}
                                          {countsTowardYield ? ' • Counts toward yield' : ' • Does not count toward yield'}
                                        </div>
                                      </div>
                                    </label>
                                  );
                                })}
                              </div>
                              {formData.individualOutfeedProductIds.length === 0 && (
                                <p className="text-xs text-amber-600 mt-2">
                                  ⚠️ Please select at least one product to display
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    ) : (
                      <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-6">
                        <p className="text-sm text-amber-800 font-medium">
                          ⚠️ Please select outfeed products first
                        </p>
                        <p className="text-xs text-amber-600 mt-1">
                          Go to the "Product Selection" tab to choose which products should be tracked as outfeed.
                        </p>
                      </div>
                    )
                  )}

                  {/* Sub-tab 2: Metrics (PPH/PPM) */}
                  {activeOutfeedSubTab === 2 && (
                    formData.outfeedProductIds.length > 0 ? (
                    <div>
                  {/* Outfeed PPH Configuration */}
                    <div className="bg-white rounded-lg border-2 border-slate-200 p-6">
                      <h4 className="text-lg font-semibold text-slate-800 mb-3">📊 Outfeed Pounds Per Hour (PPH) Display</h4>
                      <p className="text-sm text-slate-600 mb-4">Configure whether to display outfeed pounds per hour metrics in reports.</p>
                      
                      <div className="space-y-4">
                        <label className={`flex items-center p-4 rounded-lg cursor-pointer transition-colors border-2 ${
                          formData.showInfeedPph ? 'bg-gray-100 border-gray-300 cursor-not-allowed' : 'bg-blue-50 hover:bg-blue-100 border-transparent hover:border-blue-300'
                        }`}>
                          <input
                            type="checkbox"
                            checked={formData.showOutfeedPph}
                            disabled={formData.showInfeedPph}
                            onChange={(e) => {
                              if (!formData.showInfeedPph) {
                                setFormData(prev => ({ 
                                  ...prev, 
                                  showOutfeedPph: e.target.checked,
                                  outfeedPphDisplayName: e.target.checked ? prev.outfeedPphDisplayName || 'PPH' : prev.outfeedPphDisplayName
                                }));
                                setErrors(prev => ({ ...prev, showOutfeedPph: '', outfeedPphDisplayName: '' }));
                              }
                            }}
                            className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded disabled:cursor-not-allowed disabled:opacity-50"
                          />
                          <div className="ml-3">
                            <div className="text-sm font-semibold text-slate-900">Display Outfeed Pounds Per Hour</div>
                            <div className="text-xs text-slate-600">
                              {formData.showInfeedPph 
                                ? "PPH is already enabled for infeed. Disable infeed PPH to enable outfeed PPH."
                                : "Show PPH metrics for this outfeed in reports and dashboards"
                              }
                            </div>
                          </div>
                        </label>

                        {/* Outfeed PPH Display Name Input */}
                        {formData.showOutfeedPph && (
                          <div className="pt-4 border-t border-slate-200">
                            <InputField 
                              label="Outfeed PPH Display Name" 
                              name="outfeedPphDisplayName" 
                              value={formData.outfeedPphDisplayName} 
                              onChange={handleChange}
                              error={errors.outfeedPphDisplayName} 
                              required 
                            />
                            <p className="text-xs text-slate-500 mt-1">
                              This name will appear in reports when displaying outfeed pounds per hour metrics
                            </p>
                          </div>
                        )}

                        {errors.showOutfeedPph && (
                          <p className="text-xs text-red-600 font-medium">{errors.showOutfeedPph}</p>
                        )}
                      </div>
                    </div>

                  {/* Outfeed PPM Configuration */}
                    <div className="bg-white rounded-lg border-2 border-slate-200 p-6 mt-6">
                      <h4 className="text-lg font-semibold text-slate-800 mb-3">⏱️ Outfeed Pieces Per Minute (PPM) Display</h4>
                      <p className="text-sm text-slate-600 mb-4">Configure whether to display outfeed pieces per minute metrics in reports.</p>
                      
                      <div className="space-y-4">
                        <label className={`flex items-center p-4 rounded-lg cursor-pointer transition-colors border-2 ${
                          formData.showInfeedPpm ? 'bg-gray-100 border-gray-300 cursor-not-allowed' : 'bg-green-50 hover:bg-green-100 border-transparent hover:border-green-300'
                        }`}>
                          <input
                            type="checkbox"
                            checked={formData.showOutfeedPpm}
                            disabled={formData.showInfeedPpm}
                            onChange={(e) => {
                              if (!formData.showInfeedPpm) {
                                setFormData(prev => ({ 
                                  ...prev, 
                                  showOutfeedPpm: e.target.checked,
                                  outfeedPpmDisplayName: e.target.checked ? prev.outfeedPpmDisplayName || 'PPM' : prev.outfeedPpmDisplayName
                                }));
                                setErrors(prev => ({ ...prev, showOutfeedPpm: '', outfeedPpmDisplayName: '' }));
                              }
                            }}
                            className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded disabled:cursor-not-allowed disabled:opacity-50"
                          />
                          <div className="ml-3">
                            <div className="text-sm font-semibold text-slate-900">Display Outfeed Pieces Per Minute</div>
                            <div className="text-xs text-slate-600">
                              {formData.showInfeedPpm 
                                ? "PPM is already enabled for infeed. Disable infeed PPM to enable outfeed PPM."
                                : "Show PPM metrics for this outfeed in reports and dashboards"
                              }
                            </div>
                          </div>
                        </label>

                        {/* Outfeed PPM Display Name Input */}
                        {formData.showOutfeedPpm && (
                          <div className="pt-4 border-t border-slate-200">
                            <InputField 
                              label="Outfeed PPM Display Name" 
                              name="outfeedPpmDisplayName" 
                              value={formData.outfeedPpmDisplayName} 
                              onChange={handleChange}
                              error={errors.outfeedPpmDisplayName} 
                              required 
                            />
                            <p className="text-xs text-slate-500 mt-1">
                              This name will appear in reports when displaying outfeed pieces per minute metrics
                            </p>
                          </div>
                        )}

                        {errors.showOutfeedPpm && (
                          <p className="text-xs text-red-600 font-medium">{errors.showOutfeedPpm}</p>
                        )}
                      </div>
                    </div>
                    </div>
                    ) : (
                      <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-6">
                        <p className="text-sm text-amber-800 font-medium">
                          ⚠️ Please select outfeed products first
                        </p>
                        <p className="text-xs text-amber-600 mt-1">
                          Go to the "Product Selection" tab to choose which products should be tracked as outfeed.
                        </p>
                      </div>
                    )
                  )}

                </div>
              ) : (
                <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-6">
                  <p className="text-sm text-amber-800 font-medium">
                    ⚠️ Please configure pocket assignments first
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    Go to the "Pocket Assignments" tab to assign products to pockets before configuring outfeed.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Tab 4: Yield Configuration */}
          {activeTab === 4 && (
            <div className="animate-fadeIn">
              {formData.outfeedProductIds.length > 0 ? (
                <div>
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6 mb-6">
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">Yield Configuration</h3>
                    <p className="text-sm text-slate-600">Configure how yield metrics are calculated and displayed.</p>
                  </div>
                  
                  {/* Sub-tabs for Yield Configuration */}
                  <div className="border-b border-slate-200 mb-6">
                    <nav className="-mb-px flex space-x-8" aria-label="Yield sub-tabs">
                      <button
                        type="button"
                        onClick={() => setActiveYieldSubTab(0)}
                        className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                          activeYieldSubTab === 0
                            ? 'border-indigo-500 text-indigo-600'
                            : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                        }`}
                      >
                        Total Yield
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveYieldSubTab(1)}
                        className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                          activeYieldSubTab === 1
                            ? 'border-indigo-500 text-indigo-600'
                            : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                        }`}
                      >
                        Individual Yields
                      </button>
                    </nav>
                  </div>
                  
                  {/* Sub-tab 0: Total Yield */}
                  {activeYieldSubTab === 0 && (
                    <div className="bg-white rounded-lg border-2 border-slate-200 p-6">
                      <h4 className="text-lg font-semibold text-slate-800 mb-3">📊 Total Yield Configuration</h4>
                      <p className="text-sm text-slate-600 mb-4">Configure how the overall yield percentage is calculated and displayed.</p>
                      
                      <div className="space-y-6">
                        {/* Total Yield Configuration */}
                        <div className="bg-blue-50 rounded-lg p-4">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.showTotalYield}
                              onChange={(e) => {
                                setFormData(prev => ({ 
                                  ...prev, 
                                  showTotalYield: e.target.checked,
                                  totalYieldDisplayName: e.target.checked ? prev.totalYieldDisplayName || 'Total Yield' : prev.totalYieldDisplayName
                                }));
                                setErrors(prev => ({ ...prev, showTotalYield: '', totalYieldDisplayName: '' }));
                              }}
                              className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded"
                            />
                            <div className="ml-3">
                              <div className="text-sm font-semibold text-slate-900">Show Total Yield</div>
                              <div className="text-xs text-slate-600">Display overall yield percentage (Total Outfeed / Infeed)</div>
                            </div>
                          </label>

                          {formData.showTotalYield && (
                            <div className="mt-4">
                              <label className="block text-sm font-medium text-slate-700 mb-1">
                                Total Yield Display Name
                              </label>
                              <input
                                type="text"
                                value={formData.totalYieldDisplayName || ''}
                                onChange={(e) => {
                                  setFormData(prev => ({ ...prev, totalYieldDisplayName: e.target.value }));
                                  setErrors(prev => ({ ...prev, totalYieldDisplayName: '' }));
                                }}
                                placeholder="e.g., Total %, Overall Yield"
                                className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                              />
                              <p className="text-xs text-slate-500 mt-1">
                                This name will appear in reports when displaying total yield metrics
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Sub-tab 1: Individual Yields */}
                  {activeYieldSubTab === 1 && (
                    <div className="bg-white rounded-lg border-2 border-slate-200 p-6">
                      <h4 className="text-lg font-semibold text-slate-800 mb-3">📊 Individual Yield Configuration</h4>
                      <p className="text-sm text-slate-600 mb-4">Configure yield calculations for individual outfeed products.</p>
                      
                      <div className="space-y-6">
                        {/* Individual Yields Configuration */}
                        <div className="bg-green-50 rounded-lg p-4">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.showIndividualYields}
                              onChange={(e) => {
                                setFormData(prev => ({ 
                                  ...prev, 
                                  showIndividualYields: e.target.checked,
                                  individualYieldProductIds: e.target.checked ? prev.individualYieldProductIds || [] : []
                                }));
                                setErrors(prev => ({ ...prev, showIndividualYields: '' }));
                              }}
                              className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded"
                            />
                            <div className="ml-3">
                              <div className="text-sm font-semibold text-slate-900">Show Individual Product Yields</div>
                              <div className="text-xs text-slate-600">Display yield percentages for specific outfeed products</div>
                            </div>
                          </label>

                          {formData.showIndividualYields && (
                            <div className="mt-4 space-y-4">
                              <div className="text-sm font-medium text-slate-700 mb-2">
                                Select products to show individual yields for:
                              </div>

                              <div className="space-y-3">
                                {/* Show ALL pocket products for individual yields, not just outfeedProductIds */}
                                {Array.from(new Set(formData.pocketAssignments.map(pa => pa.productId).filter(id => id))).map(productId => {
                                  const product = allProducts?.find(p => p.id === productId);
                                  const isSelected = formData.individualYieldProductIds.includes(productId);
                                  const countsTowardYield = formData.outfeedProductIds.includes(productId);
                                  const pocketNumbers = formData.pocketAssignments
                                    .filter(pa => pa.productId === productId)
                                    .map(pa => pa.pocketNumber);
                                  
                                  return (
                                    <div key={productId} className="border border-slate-200 rounded-lg p-3">
                                      <label className="flex items-center cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={(e) => {
                                            const newProductIds = e.target.checked 
                                              ? [...formData.individualYieldProductIds, productId]
                                              : formData.individualYieldProductIds.filter(id => id !== productId);
                                            
                                            setFormData(prev => ({ 
                                              ...prev, 
                                              individualYieldProductIds: newProductIds
                                            }));
                                          }}
                                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-slate-300 rounded"
                                        />
                                        <div className="ml-3 flex-1">
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-slate-900">
                                              {product?.name || `Product ${productId}`}
                                            </span>
                                            {pocketNumbers.length > 0 && (
                                              <span className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">
                                                Pocket {pocketNumbers.join(', ')}
                                              </span>
                                            )}
                                            {!countsTowardYield && (
                                              <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">
                                                Does not count toward total yield
                                              </span>
                                            )}
                                          </div>
                                          <div className="text-xs text-slate-500">
                                            {product?.productId || productId}
                                          </div>
                                        </div>
                                      </label>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">📦</div>
                  <p className="text-lg font-medium text-slate-700 mb-2">No Pocket Assignments</p>
                  <p className="text-sm text-slate-500">Please assign products to pockets first in the "Pocket Assignments" tab.</p>
                </div>
              )}
            </div>
          )}

          {/* Tab 5: Summary Metrics */}
          {activeTab === 5 && (
            <DashboardMetricsTab 
              metrics={formData.dashboardMetrics}
              onChange={(metrics) => setFormData(prev => ({ ...prev, dashboardMetrics: metrics }))}
            />
          )}

          {/* Tab 6: Column Configuration */}
          {activeTab === 6 && (
            <div className="space-y-8 pb-6">
              <div className="bg-white rounded-lg border-2 border-slate-200 p-6">
                <h4 className="text-lg font-semibold text-slate-800 mb-3">📐 Column Order</h4>
                <p className="text-sm text-slate-600 mb-4">Configure which columns are displayed and their order in reports and dashboards. Drag and drop to reorder columns.</p>
                
                <ColumnConfigurationSection 
                  formData={formData}
                  allProducts={allProducts}
                  onColumnOrderChange={(newConfiguration) => {
                    setFormData(prev => ({ ...prev, columnConfiguration: newConfiguration }));
                  }}
                />
              </div>
            </div>
          )}
        </div>
        
        {/* Footer Buttons - Fixed at bottom */}
        <div className="flex-shrink-0 px-6 -mx-6 pt-4 pb-4 border-t border-slate-200 bg-white flex justify-between items-center">
          <div className="text-xs text-slate-500">
            {activeTab < tabs.length - 1 && (
              <span>Tip: Use tabs to navigate between sections</span>
            )}
          </div>
          
          {/* Validation Error Summary */}
          {Object.values(errors).some(error => error) && (
            <div className="mb-4 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Please fix the following validation errors:
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    <ul className="list-disc pl-5 space-y-1">
                      {errors.groupName && <li>Group name is required</li>}
                      {errors.groupId && <li>Group ID is required</li>}
                      {errors.infeedProductId && <li>Infeed product must be selected</li>}
                      {errors.pocketAssignments && <li>{errors.pocketAssignments}</li>}
                      {errors.outfeedProductIds && <li>At least one outfeed product must be selected</li>}
                      {errors.infeedBatchWeight && <li>Infeed batch weight must be a positive number</li>}
                      {Object.entries(errors).filter(([key, value]) => value && !['groupName', 'groupId', 'infeedProductId', 'pocketAssignments', 'outfeedProductIds', 'infeedBatchWeight'].includes(key)).map(([key, value]) => (
                        <li key={key}>{value}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex space-x-3">
            {activeTab > 0 && (
              <button
                type="button"
                onClick={() => setActiveTab(activeTab - 1)}
                className="px-5 py-2.5 text-sm font-semibold text-slate-600 bg-white hover:bg-slate-50 border-2 border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-400 transition-all duration-200"
              >
                ← Previous
              </button>
            )}
            {activeTab < tabs.length - 1 && (
              <button
                type="button"
                onClick={() => setActiveTab(activeTab + 1)}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-indigo-500 hover:bg-indigo-600 border-2 border-indigo-500 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
              >
                Next →
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-semibold text-slate-600 bg-white hover:bg-slate-50 border-2 border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-400 transition-all duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={() => console.log('Save button clicked')}
              className="px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 border-2 border-transparent rounded-lg shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 transform hover:scale-105"
            >
              💾 Save Group
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default GroupFormModal;